import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

/**
 * useFacetedExplorer — generic hook for explorer/registry UIs.
 *
 * Provides:
 * - Multi-criteria filtering (search × facet groups × status)
 * - Sort by usage / alphabetical
 * - localStorage persistence for view-mode and sort-mode
 * - Snapshot/restore of expanded categories across filter sessions
 * - Force-open during active filtering so results stay visible
 *
 * Originally implemented in `portals/admin/.../useRegistryFilters.ts`.
 * Promoted to shared per ADMIN_DOMAIN_RC_AUDIT_2026 §19.4 P1-1.
 *
 * Consumers should define their own item-type that extends `FacetedItem`.
 *
 * NOTE: The `modules` field is named for backwards-compat with the admin
 * question-bank explorer. Treat it as a generic "facet group" — e.g.
 * subjects, tags, classrooms, or any cross-cutting categorisation.
 */

export interface FacetedItem {
  id: string
  label: string
  /** Number of references in the underlying data (drives "active/unused"). */
  usageCount: number
  /** True if the item exists in the canonical registry/source-of-truth. */
  isRegistered: boolean
  /** Generic facet group used by the "modules" filter. */
  modules: Set<string>
}

export type FacetStatus = 'active' | 'unused' | 'unregistered'

export interface FacetedFilterState {
  searchQuery: string
  modules: string[]
  status: FacetStatus[]
}

export type FacetedSortMode = 'usage' | 'alpha'

export type FacetedViewMode = 'cards' | 'compact'

export interface UseFacetedExplorerOptions {
  /** Prefix for localStorage keys. Omit to disable persistence. */
  storageKey?: string
  /** Category ids that should start expanded on first render. */
  defaultExpandedIds?: string[]
}

export function useFacetedExplorer<T extends FacetedItem>(
  items: T[],
  options: UseFacetedExplorerOptions = {},
) {
  const { storageKey, defaultExpandedIds = [] } = options

  const [filters, setFilters] = useState<FacetedFilterState>({
    searchQuery: '',
    modules: [],
    status: [],
  })

  // ── view mode (persisted) ─────────────────────────────────
  const [viewMode, setViewModeState] = useState<FacetedViewMode>(() => {
    if (typeof window === 'undefined' || !storageKey) return 'cards'
    try {
      const stored = window.localStorage.getItem(`${storageKey}.viewMode`)
      return stored === 'compact' ? 'compact' : 'cards'
    } catch {
      return 'cards'
    }
  })

  const setViewMode = useCallback(
    (mode: FacetedViewMode) => {
      setViewModeState(mode)
      if (typeof window !== 'undefined' && storageKey) {
        try {
          window.localStorage.setItem(`${storageKey}.viewMode`, mode)
        } catch {
          /* ignore */
        }
      }
    },
    [storageKey],
  )

  // ── sort mode (persisted) ─────────────────────────────────
  const [sortMode, setSortModeState] = useState<FacetedSortMode>(() => {
    if (typeof window === 'undefined' || !storageKey) return 'usage'
    try {
      const stored = window.localStorage.getItem(`${storageKey}.sortMode`)
      return stored === 'alpha' ? 'alpha' : 'usage'
    } catch {
      return 'usage'
    }
  })

  const setSortMode = useCallback(
    (mode: FacetedSortMode) => {
      setSortModeState(mode)
      if (typeof window !== 'undefined' && storageKey) {
        try {
          window.localStorage.setItem(`${storageKey}.sortMode`, mode)
        } catch {
          /* ignore */
        }
      }
    },
    [storageKey],
  )

  // ── filtered items ────────────────────────────────────────
  const filteredItems = useMemo<T[]>(() => {
    const filtered = items.filter((item) => {
      if (filters.searchQuery) {
        const query = filters.searchQuery.toLowerCase()
        if (
          !item.id.toLowerCase().includes(query) &&
          !item.label.toLowerCase().includes(query)
        ) {
          return false
        }
      }
      if (filters.modules.length > 0) {
        const hasModule = Array.from(item.modules).some((m) =>
          filters.modules.includes(m),
        )
        if (!hasModule) return false
      }
      if (filters.status.length > 0) {
        const isActive = item.usageCount > 0
        const isUnused = item.usageCount === 0 && item.isRegistered
        const isUnregistered = !item.isRegistered
        const matchesStatus =
          (filters.status.includes('active') && isActive) ||
          (filters.status.includes('unused') && isUnused) ||
          (filters.status.includes('unregistered') && isUnregistered)
        if (!matchesStatus) return false
      }
      return true
    })

    if (sortMode === 'alpha') {
      return [...filtered].sort((a, b) => a.id.localeCompare(b.id))
    }
    return filtered
  }, [items, filters, sortMode])

  const availableModules = useMemo(() => {
    const mods = new Set<string>()
    items.forEach((item) => {
      item.modules.forEach((m) => mods.add(m))
    })
    return Array.from(mods).sort()
  }, [items])

  const hasActiveFilters =
    filters.searchQuery.trim().length > 0 ||
    filters.modules.length > 0 ||
    filters.status.length > 0

  const clearFilters = useCallback(() => {
    setFilters({ searchQuery: '', modules: [], status: [] })
  }, [])

  // ── expanded categories ───────────────────────────────────
  // Single source of truth: which categories are currently open.
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(
    () => new Set(defaultExpandedIds),
    // eslint-disable-next-line react-hooks/exhaustive-deps
  )

  const toggleCategory = useCallback((id: string) => {
    setExpandedCategories((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }, [])

  const expandAll = useCallback((ids: string[]) => {
    setExpandedCategories(new Set(ids))
  }, [])

  const collapseAll = useCallback(() => {
    setExpandedCategories(new Set())
  }, [])

  // Snapshot/restore expand state across filter sessions so the user's
  // manual layout is preserved when they clear filters.
  const prevHasFilters = useRef(false)
  const snapshotBeforeFilter = useRef<Set<string> | null>(null)

  useEffect(() => {
    if (hasActiveFilters && !prevHasFilters.current) {
      snapshotBeforeFilter.current = new Set(expandedCategories)
    } else if (!hasActiveFilters && prevHasFilters.current) {
      if (snapshotBeforeFilter.current) {
        setExpandedCategories(snapshotBeforeFilter.current)
        snapshotBeforeFilter.current = null
      }
    }
    prevHasFilters.current = hasActiveFilters
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasActiveFilters])

  const isCategoryExpanded = useCallback(
    (id: string): boolean => {
      if (hasActiveFilters) return true // force-open when filtering
      return expandedCategories.has(id)
    },
    [hasActiveFilters, expandedCategories],
  )

  return {
    filters,
    setFilters,
    viewMode,
    setViewMode,
    sortMode,
    setSortMode,
    filteredItems,
    availableModules,
    hasActiveFilters,
    clearFilters,
    expandedCategories,
    toggleCategory,
    expandAll,
    collapseAll,
    isCategoryExpanded,
  }
}
